import React, { useContext, useEffect, useState } from 'react';
import { Box, Column, Tab } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import DealerLocation from '../shared/DealerLocation';
import DealerSchedule from '../DealerSchedule';
import DealerBadges from '../shared/DealerBadges';
import formatContactNumber from '../../utils/formatContactNumber';
import formatContactUrlDomain from '../../utils/formatContactURLDomain';
import themeStyles from './DepartmentTabs.style';
import DealerContact, { DealerContactDetail } from '../shared/DealerContact';
import dealerCardGtmTags, { CLICKTO_CALL, MAILTO, WEBSITE } from '../../utils/gtmTags';
import { DEPARTMENT_TYPE } from '../../utils/constants';
import { DealersListContext } from '../DealersListContext';
import dealerPropTypes from '../../utils/interfaces';

const Container = themeStyles.apply(Box, 'Container');
const DepartmentsTabs = themeStyles.apply(Box, 'DepartmentsTabs');
const DepartmentLabel = themeStyles.apply(Box, 'DepartmentLabel');
const DepartmentInformation = themeStyles.apply(Box, 'DepartmentInformation');
const LeftColumn = themeStyles.apply(Box, 'LeftColumn');

const DepartmentTabs = ({ dealer, variant }) => {
  const [currentDepartmentType, setCurrentDepartmentType] = useState(DEPARTMENT_TYPE.SALES);
  const { selectedDealer } = useContext(DealersListContext);
  const { t } = useTranslation();

  useEffect(() => () => setCurrentDepartmentType(DEPARTMENT_TYPE.SALES), [setCurrentDepartmentType]);

  if (!dealer) return null;

  const { departments: dealerDepartments, location, greenDealer, evDealer } = dealer || {};
  const currentDepartment = dealerDepartments?.find(department => department?.type === currentDepartmentType);
  const phone = currentDepartment?.contactInformation?.phone;
  const email = currentDepartment?.contactInformation?.email;
  const fax = currentDepartment?.contactInformation?.fax;
  const website = currentDepartment?.contactInformation?.website || selectedDealer?.contactInformation?.website;

  return (
    <Container>
      <DepartmentsTabs data-testid="cy-departments-tabs">
        {dealerDepartments.map(department => (
          <Column key={department?.type}>
            <Tab
              data-testid="cy-department-type"
              isActive={currentDepartmentType === department?.type}
              onClick={() => setCurrentDepartmentType(department?.type)}
              backgroundColor="transparent"
            >
              <DepartmentLabel>{t(`Sub-apps.Dealers.${department?.type}Label`)}</DepartmentLabel>
            </Tab>
          </Column>
        ))}
      </DepartmentsTabs>
      <DepartmentInformation>
        <Box display="flex">
          <LeftColumn>
            <DealerLocation address={location} hasBadge={greenDealer || evDealer} />
            <DealerContact>
              <DealerContactDetail
                contact={formatContactNumber(phone)}
                prefix="tel"
                type="phone"
                gtmTags={dealerCardGtmTags?.Call}
                dealer={dealer}
                gtmActionType={CLICKTO_CALL}
              />
              <DealerContactDetail contact={formatContactNumber(fax)} type="fax" />
              <DealerContactDetail
                contact={formatContactUrlDomain(email)}
                prefix="mailto"
                type="email"
                gtmTags={dealerCardGtmTags?.Email}
                dealer={dealer}
                gtmActionType={MAILTO}
              />
              <DealerContactDetail
                contact={formatContactUrlDomain(website)}
                type="website"
                gtmTags={dealerCardGtmTags?.Website}
                dealer={dealer}
                gtmActionType={WEBSITE}
              />
            </DealerContact>
          </LeftColumn>
          <DealerBadges greenDealer={greenDealer} evDealer={evDealer} variant={variant} />
        </Box>
        <DealerSchedule currentDepartmentType={currentDepartmentType} departments={dealerDepartments} />
      </DepartmentInformation>
    </Container>
  );
};

export default DepartmentTabs;

DepartmentTabs.propTypes = {
  variant: PropTypes.string.isRequired,
  dealer: dealerPropTypes,
};
